import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Switch } from "../../ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { LockedPreview } from "../LockedPreview";
import { VoiceHint } from "../VoiceHint";
import { Calculator, Target, Lock, Scale, Flame, Zap, AlertTriangle, Dumbbell } from "lucide-react";
import { IconWrapper } from "../IconWrapper";
import { useEffect } from "react";
import { trackEvent } from "../../../config/analytics";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/tooltip";

interface GoalsMacrosProfile {
  goal: string;
  macros: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  proteinPriority: boolean;
}

interface GoalsMacrosSectionProps {
  profile: GoalsMacrosProfile;
  onUpdate: (updates: Partial<GoalsMacrosProfile>) => void;
  currentTier: string;
  isLocked: boolean;
}

export function GoalsMacrosSection({ 
  profile, 
  onUpdate, 
  currentTier, 
  isLocked 
}: GoalsMacrosSectionProps) {
  
  if (isLocked) {
    return (
      <LockedPreview
        title="Level up your kitchen game with nutrition targets"
        description="Track macros, set fitness goals, and get smart nutrition guidance with Family."
        targetTier="core"
        onUpgrade={() => console.log("Upgrade to Family")}
      />
    );
  }

  const isLimited = currentTier === 'starter';

  const autoCalculateMacros = () => {
    // Simple auto-calculation based on goal
    let calories = 2000;
    let protein = 150;
    let carbs = 200;
    let fat = 65;

    switch (profile.goal) {
      case "fat-loss":
        calories = 1800;
        protein = 160;
        carbs = 150;
        fat = 60;
        break;
      case "muscle-gain":
        calories = 2400;
        protein = 180;
        carbs = 280;
        fat = 80;
        break;
      case "maintain":
        calories = 2000;
        protein = 150;
        carbs = 200;
        fat = 65;
        break;
    }

    if (isLimited) {
      // In Starter, keep it simple: adjust only calories
      onUpdate({ macros: { ...profile.macros, calories } });
    } else {
      onUpdate({ macros: { calories, protein, carbs, fat } });
    }
  };

  // Analytics: show limited macros notice once for starter
  useEffect(() => {
    if (isLimited) {
      try { trackEvent('profile_macros_limited_shown', { tier: 'starter' }) } catch {}
    }
  }, [isLimited]);

  const updateMacro = (macro: keyof typeof profile.macros, value: number) => {
    onUpdate({
      macros: {
        ...profile.macros,
        [macro]: value
      }
    });
    try { trackEvent('profile_macro_toggle', { macro, allowed: !isLimited }) } catch {}
  };

  const totalMacroCalories = (profile.macros.protein * 4) + (profile.macros.carbs * 4) + (profile.macros.fat * 9);
  const isInconsistent = Math.abs(totalMacroCalories - profile.macros.calories) > 50;

  return (
    <div className="space-y-8">
      {isLimited && (
        <div className="p-3 border border-coral-200 bg-coral-50 rounded-md text-xs text-coral-900">
          Duo includes one fitness goal and a calorie target. Unlock full macro control (protein, carbs, fat) with Family.
        </div>
      )}
      <VoiceHint 
        message="Now we're cooking with science! Set your nutrition goals and I'll make sure every meal moves you toward your targets. 🎯"
        voice="medium"
      />

      {/* Fitness Goal */}
      <div className="space-y-4">
        <Label className="text-base flex items-center gap-2">
          <Target className="h-4 w-4" />
          Fitness Goal
        </Label>
        <RadioGroup
          value={profile.goal}
          onValueChange={(value) => onUpdate({ goal: value })}
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {[
            { 
              value: "fat-loss", 
              label: "Fat Loss", 
              desc: "Lose weight while maintaining muscle",
              icon: Flame
            },
            { 
              value: "maintain", 
              label: "Maintain", 
              desc: "Stay healthy and energized",
              icon: Scale
            },
            { 
              value: "muscle-gain", 
              label: "Muscle Gain", 
              desc: "Build lean muscle mass",
              icon: Dumbbell
            }
          ].map(({ value, label, desc, icon }) => (
            <div key={value} className="flex items-center space-x-2 border rounded-lg p-4">
              <RadioGroupItem value={value} id={`goal-${value}`} />
              <div className="flex-1">
                <Label htmlFor={`goal-${value}`} className="text-sm font-medium flex items-center gap-2">
                  <IconWrapper icon={icon} />
                  {label}
                </Label>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Macro Targets */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              Macro Targets
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={autoCalculateMacros}
              disabled={!profile.goal || isLimited}
            >
              Auto-calc
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Calories */}
            <div className="space-y-2">
              <Label htmlFor="calories" className="text-sm">Calories</Label>
              <div className="relative">
                <Input
                  id="calories"
                  type="number"
                  value={profile.macros.calories}
                  onChange={(e) => updateMacro('calories', parseInt(e.target.value) || 0)}
                  className="pr-12"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
                  kcal
                </span>
              </div>
            </div>

            {/* Protein */}
            <div className="space-y-2">
              <Label htmlFor="protein" className="text-sm flex items-center gap-1">Protein {isLimited && <span className="text-[10px] text-muted-foreground">(Family+)</span>} {isLimited && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>Full macro control is available on the Family plan.</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}</Label>
              <div className="relative">
                <Input
                  id="protein"
                  type="number"
                  value={profile.macros.protein}
                  onChange={(e) => updateMacro('protein', parseInt(e.target.value) || 0)}
                  className="pr-8"
                  disabled={isLimited}
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
                  g
                </span>
              </div>
            </div>

            {/* Carbs */}
            <div className="space-y-2">
              <Label htmlFor="carbs" className="text-sm flex items-center gap-1">Carbs {isLimited && <span className="text-[10px] text-muted-foreground">(Family+)</span>} {isLimited && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>Full macro control is available on the Family plan.</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}</Label>
              <div className="relative">
                <Input
                  id="carbs"
                  type="number"
                  value={profile.macros.carbs}
                  onChange={(e) => updateMacro('carbs', parseInt(e.target.value) || 0)}
                  className="pr-8"
                  disabled={isLimited}
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
                  g
                </span>
              </div>
            </div>

            {/* Fat */}
            <div className="space-y-2">
              <Label htmlFor="fat" className="text-sm flex items-center gap-1">Fat {isLimited && <span className="text-[10px] text-muted-foreground">(Family+)</span>} {isLimited && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>Full macro control is available on the Family plan.</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}</Label>
              <div className="relative">
                <Input
                  id="fat"
                  type="number"
                  value={profile.macros.fat}
                  onChange={(e) => updateMacro('fat', parseInt(e.target.value) || 0)}
                  className="pr-8"
                  disabled={isLimited}
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground">
                  g
                </span>
              </div>
            </div>
          </div>

          {/* Macro Breakdown */}
          {!isLimited && (
            <div className="grid grid-cols-3 gap-4 text-center text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Protein</div>
                <div className="font-medium">{Math.round((profile.macros.protein * 4 / profile.macros.calories) * 100)}%</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Carbs</div>
                <div className="font-medium">{Math.round((profile.macros.carbs * 4 / profile.macros.calories) * 100)}%</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Fat</div>
                <div className="font-medium">{Math.round((profile.macros.fat * 9 / profile.macros.calories) * 100)}%</div>
              </div>
            </div>
          )}

          {/* Inconsistency Warning */}
          {!isLimited && isInconsistent && (
            <div className="p-3 bg-coral-50 border border-coral-200 rounded-lg">
              <p className="text-sm text-warning-700">
                <AlertTriangle className="h-4 w-4 inline mr-1" /> Your macro calories ({totalMacroCalories}) don't match your target calories ({profile.macros.calories}). 
                Consider using auto-calc or adjusting manually.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Protein Priority */}
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div className="space-y-1">
          <Label className="text-sm font-medium">Protein Priority</Label>
          <p className="text-xs text-muted-foreground">
            Prioritize high-protein recipes to help reach your target
          </p>
        </div>
        <Switch
          checked={profile.proteinPriority}
          onCheckedChange={(checked) => onUpdate({ proteinPriority: checked })}
          disabled={isLimited}
        />
      </div>

      {isLimited && (
        <div className="text-xs text-muted-foreground">
          Want full macro control and protein priority? Upgrade to Family.
        </div>
      )}

      {/* Goal-specific Hint */}
      {profile.goal && (
        <VoiceHint 
          message={
            profile.goal === "fat-loss" 
              ? "Perfect! I'll focus on nutrient-dense, lower-calorie meals that keep you satisfied."
              : profile.goal === "muscle-gain"
              ? "Great choice! I'll include plenty of protein-rich meals to fuel your gains."
              : "Excellent! I'll create balanced meals to keep your energy steady throughout the day."
          }
          voice="medium"
        />
      )}
    </div>
  );
}
